import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fetchEWODiscripency } from "../../action/EWODiscripencyAction";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = "#330066";

const Page = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "0 0 80px",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

const formatDate = (d) => new Date(d).toISOString().split("T")[0];
const formatTime = (d) =>
  new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

export default function EwoDiscrepancy() {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((s) => s.ewoDiscripency);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    dispatch(fetchEWODiscripency(formatDate(selectedDate)));
  }, [dispatch, selectedDate]);

  const sortedData = React.useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) =>
      (a.EWODis_supName || "").localeCompare(b.EWODis_supName || "")
    );
  }, [data]);

  const totalEmployees = sortedData.length;

  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text("EWO Discrepancy Report", pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const y = 44, sx = 15, tw = pw - 30;
    [
      [sx + tw * 0.167, "Date – ",          formatDate(selectedDate)],
      [sx + tw * 0.5,   "Time – ",          formatTime(at)],
      [sx + tw * 0.833, "Total Records – ", totalEmployees.toString()],
    ].forEach(([col, bold, val]) => {
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, y);
      doc.setFont("helvetica", "normal").text(val, col - fw / 2 + doc.getTextWidth(bold), y);
    });

    autoTable(doc, {
      head: [["IWO No", "Supervisor", "Task"]],
      body: sortedData.map((item) => [
        item.EWODis_iwoNo || "",
        item.EWODis_supName || "",
        item.EWODis_engSpec || "",
      ]),
      startY: 52,
      styles: { fontSize: 12, cellPadding: 5 },
      headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 30, halign: "center" },
        1: { cellWidth: 60 },
        2: { cellWidth: 97 },
      },
      theme: "grid",
      margin: { left: 13, right: 10 },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`EWO_Discrepancy_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Page>
        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36, borderRadius: "20px", border: "0.5px solid #e2e8f0",
              background: "#fff", display: "flex", alignItems: "center",
              gap: "6px", px: "12px", cursor: "pointer", flexShrink: 0,
              "&:hover": { borderColor: BRAND },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={(date) => { setSelectedDate(date); setOpenDatePicker(false); }}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
            />
          )}
        </Box>

        {/* ── Stats row ── */}
        {!loading && sortedData.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <Box sx={{ flex: 1, background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: "12px", p: "10px 12px" }}>
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>Total Records</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 600, color: BRAND, lineHeight: 1 }}>
                {totalEmployees}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── PDF button ── */}
        {!loading && sortedData.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleExportPDF}
              disabled={!sortedData.length}
              sx={{
                flex: 1, height: 34, borderRadius: "20px",
                background: "#16a34a", color: "#fff", fontSize: 12,
                fontWeight: 500, textTransform: "none",
                "&:hover": { background: "#15803d" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              Export PDF
            </Button>
          </Box>
        )}

        {/* ── Table ── */}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: BRAND }} />
          </Box>
        ) : (
          <Card>
            <TableContainer sx={{ maxHeight: "calc(100vh - 260px)", overflowX: "auto" }}>
              <Table size="small" stickyHeader sx={{ minWidth: 360 }}>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "IWO No",     w: "20%", align: "center" },
                      { label: "Supervisor", w: "35%", align: "left"   },
                      { label: "Task",       w: "45%", align: "left"   },
                    ].map((h) => (
                      <TableCell
                        key={h.label}
                        sx={{
                          width: h.w, background: BRAND, color: "#fff",
                          fontSize: 11, fontWeight: 600, py: "8px", px: "10px",
                          borderBottom: "none", textAlign: h.align,
                          position: "sticky", top: 0, zIndex: 10,
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedData.length > 0 ? (
                    sortedData.map((item, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          "&:nth-of-type(even) td": { background: "#f8faff" },
                          "&:hover td": { background: "rgba(21,76,121,0.04)", cursor: "pointer" },
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        {/* IWO No */}
                        <TableCell sx={{ textAlign: "center", px: "10px", py: "8px" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block", px: "8px", py: "2px",
                              borderRadius: "10px", fontSize: 11, fontWeight: 600,
                              background: "#e8f0fe", color: BRAND,
                            }}
                          >
                            {item.EWODis_iwoNo}
                          </Box>
                        </TableCell>

                        {/* Supervisor */}
                        <TableCell
                          sx={{
                            fontSize: 12, fontWeight: 500, px: "10px", py: "8px",
                            whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3,
                          }}
                        >
                          {item.EWODis_supName}
                        </TableCell>

                        {/* Task */}
                        <TableCell
                          sx={{
                            fontSize: 11, color: "#475569", px: "10px", py: "8px",
                            whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3,
                          }}
                        >
                          {item.EWODis_engSpec}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}
                      >
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Page>
    </LocalizationProvider>
  );
}